import * as dateUtils from '@/common/dateUtils'
import * as uom from '@/common/uom'

export default {}

/**
 * Converts seconds into HH:MM:SS
 *
 * @param seconds
 * @returns {string}
 */
export const secsToHMS = (seconds) => {
  const h = Math.floor(seconds / 3600).toString().padStart(2, '0')
  const m = Math.floor((seconds / 60) % 60).toString().padStart(2, '0')
  const s = Math.floor(seconds % 60).toString().padStart(2, '0')
  return `${h}:${m}:${s}`
}

/**
 * Converts a duration string into seconds. The duration string must
 * be in the format HH:MM:SS
 *
 * @param duration String
 * @returns {number}
 */
export const HMSToSecs = (duration) => {
  const hms = duration.split(':')
  return (parseInt(hms[0], 10) * 3600) + (parseInt(hms[1], 10) * 60) + parseInt(hms[2])
}

/**
 * Returns a specified sensors value from the sensors array in telemetry data.
 *
 * @param name String
 * @param telemetry Object
 *
 * @returns mixed
 */
export const fromTelemetry = (name, telemetry) => {
  const sensorIdx = telemetry.sensors.findIndex((sensor) => {
    return sensor[0] === name
  })
  if (sensorIdx === -1) {
    return null
  } else {
    return {
      local_time: dateUtils.time(telemetry.gps.local_time),
      value: telemetry.sensors[sensorIdx][1]
    }
  }
}

/**
 * Returns the sensor's max value from the summary data in a trip.
 *
 * @param name String
 * @param trip Object
 *
 * @returns mixed
 */
export const maxFromTrip = (name, trip) => {
  const sensorIdx = trip.summary.sensor_ranges.findIndex((summary) => {
    return summary.name === name
  })
  if (sensorIdx === -1) {
    return null
  } else {
    return trip.summary.sensor_ranges[sensorIdx].max
  }
}

/**
 * Returns the sensor's min value from the summary data in a trip.
 *
 * @param name String
 * @param trip Object
 *
 * @returns mixed
 */
export const minFromTrip = (name, trip) => {
  const sensorIdx = trip.summary.sensor_ranges.findIndex((summary) => {
    return summary.name === name
  })
  if (sensorIdx === -1) {
    return null
  } else {
    return trip.summary.sensor_ranges[sensorIdx].min
  }
}

/**
 * Returns the sensor's avg value from the summary data in a trip.
 *
 * @param name String
 * @param trip Object
 *
 * @returns mixed
 */
export const avgFromTrip = (name, trip) => {
  const sensorIdx = trip.summary.sensor_ranges.findIndex((summary) => {
    return summary.name === name
  })
  if (sensorIdx === -1) {
    return null
  } else {
    return trip.summary.sensor_ranges[sensorIdx].avg
  }
}

export const transformAnalogInputs = (analogInputCfg, an_inputs) => {
  let results = []
  if (analogInputCfg === null || typeof analogInputCfg === 'undefined') {
    an_inputs.forEach((an_input) => {
      results.push(an_input)
    })
    return an_inputs
  }

  an_inputs.forEach((an_input, index) => {
    const cfg = analogInputCfg.find((inputCfg) => index + 1 === inputCfg.input_no)
    if (typeof cfg !== 'undefined' && cfg.input_type === 'an_inputs') {
      const value = uom.uomConvertFromStandard(an_input * cfg.factor, cfg.uom)
      if (cfg.low_raw_limit !== null && cfg.high_raw_limit !== null) {
        // Setup range to be from 0 to x (instead of a possible negative number to x)
        const range = cfg.high_raw_limit + Math.abs(cfg.low_raw_limit)
        const percentage = (((an_input + Math.abs(cfg.low_raw_limit)) / range) * 100).toFixed(1)
        results.push(uom.uomShortFormat(value, cfg.uom) + ` (${percentage}%)`)
      } else {
        results.push(uom.uomShortFormat(value, cfg.uom))
      }
    } else {
      results.push(an_input)
    }
  })

  return results
}

export const getAnalogInputLabel = (analogInputCfg, input_no) => {
  if (analogInputCfg === null || typeof analogInputCfg === 'undefined') {
    return ''
  }

  const cfg = analogInputCfg.find((inputCfg) => input_no === inputCfg.input_no)
  if (typeof cfg !== 'undefined') {
    return cfg.label
  }

  return ''
}

export const analogInputSensorNames = [
  { key: 'an_inputs', value: 'Onboard Analog Input' },
  { key: 'temperature', value: 'Onboard Temperature' },
  { key: 'ble_temp', value: 'BLE Temperature' },
  { key: 'lls_temp', value: 'LLS Temperature' },
  { key: 'eye_temp', value: 'EYE Temperature' },
  { key: 'eye_humidity', value: 'EYE Humidity' },
  { key: 'dallas_temp', value: 'Dallas Temperature' },
  { key: 'lls_fuel_level', value: 'LLS Fuel Level' }
]

const analogToPtoStatus = (inputNo, statusFlags) => {
  switch (statusFlags[inputNo - 1]) {
    case 0:
      return `PTO ${inputNo}: Off`
    case 1:
      return `PTO ${inputNo}: On`
    case 2:
      return `PTO ${inputNo}: Indeterminate`
    default:
      return ''
  }
}

// Formatters for the sensors GWS decodes with a CAN profile. GWS sends
// [name, null, error] for a sensor it could not read: that is "No reading".
const canNumber = (decimals, unit) => (value) => {
  if (value === null) {
    return 'No reading'
  }

  const number = decimals === null ? `${value}` : Number(value).toFixed(decimals)
  return unit === '' ? number : `${number} ${unit}`
}

const canBoolean = (value) => {
  if (value === null) {
    return 'No reading'
  }

  return value ? 'Yes' : 'No'
}

const canEnum = (labels) => (value) => {
  if (value === null) {
    return 'No reading'
  }

  return labels.hasOwnProperty(value) ? labels[value] : value
}

export const sensors = {
  'gps.satellites': {
    label: 'GPS Satellites',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => {
      return {
        local_time: dateUtils.time(telemetry.gps.local_time),
        value: telemetry.gps.satellites
      }
    },
    chartType: 'line',
    category: 'gps'
  },

  'gps.speed': {
    label: 'GPS Speed',
    format: (value) => {
      return `${value} km/h`
    },
    get: (telemetry) => {
      return {
        local_time: dateUtils.time(telemetry.gps.local_time),
        value: telemetry.gps.speed
      }
    },
    chartType: 'line',
    category: 'gps'
  },

  'ignition': {
    label: 'Ignition',
    format: (value) => {
      switch (value) {
        case 'acc':
          return 'ACC'

        case 'on':
          return 'On'

        case 'off':
          return 'Off'

        default:
          return value
      }
    },
    get: (telemetry) => fromTelemetry('ignition', telemetry),
    chartType: null,
    category: 'general'
  },

  'ebat_volts': {
    label: 'Ext. Batt',
    format: (value) => {
      return `${value} V`
    },
    get: (telemetry) => fromTelemetry('ebat_volts', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'ebat_perc': {
    label: 'Ext. Batt. %',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('ebat_perc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'ibat_volts': {
    label: 'Int. Batt',
    format: (value) => {
      return `${value} V`
    },
    get: (telemetry) => fromTelemetry('ibat_volts', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'ibat_perc': {
    label: 'Int. Batt. %',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('ibat_perc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'ibutton': {
    label: 'IButton',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ibutton', telemetry),
    chartType: null,
    category: 'driver'
  },

  'fuel_consumed': {
    label: 'Fuel Consumed',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('fuel_consumed', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'eng_work_time': {
    label: 'Eng. Work time',
    format: (value) => {
      return secsToHMS(value)
    },
    get: (telemetry) => fromTelemetry('eng_work_time', telemetry),
    chartType: null,
    category: 'engine'
  },

  'eng_load_perc': {
    label: 'Eng. Load Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('eng_load_perc', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'tach_tot_dist_km': {
    label: 'Tacho Tot. Dist.',
    format: (value) => {
      return `${value} km`
    },
    get: (telemetry) => fromTelemetry('tach_tot_dist_km', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'eng_tot_fuel_used_l': {
    label: 'Total Fuel Used',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('eng_tot_fuel_used_l', telemetry),
    chartType: null,
    category: 'fuel'
  },

  'fuel_rate_lh': {
    label: 'Fuel Rate ℓ/h',
    format: (value) => {
      return `${value} ℓ/h`
    },
    get: (telemetry) => fromTelemetry('fuel_rate_lh', telemetry),
    chartType: null,
    category: 'fuel'
  },

  'inst_fuel_econ_kml': {
    label: 'Fuel Economy km/ℓ',
    format: (value) => {
      return `${value} km/ℓ`
    },
    get: (telemetry) => fromTelemetry('inst_fuel_econ_kml', telemetry),
    chartType: null,
    category: 'fuel'
  },

  'eco_score': {
    label: 'Eco score',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('eco_score', telemetry),
    chartType: null,
    category: 'general'
  },

  'speed': {
    label: 'Speed',
    format: (value) => {
      return `${value} km/h`
    },
    get: (telemetry) => fromTelemetry('speed', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'ibat_amps': {
    label: 'Int. Batt Amps',
    format: (value) => {
      return `${value} A`
    },
    get: (telemetry) => fromTelemetry('ibat_amps', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'lvcan_speed': {
    label: 'LVCAN Speed',
    format: (value) => {
      return `${value} km/h`
    },
    get: (telemetry) => fromTelemetry('lvcan_speed', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'acc_pedal_pos': {
    label: 'Acc Pedal / Throttle Position',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('acc_pedal_pos', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'fuel_lvl': {
    label: 'Fuel Level',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('fuel_lvl', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'eng_rpm': {
    label: 'Eng. RPM',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('eng_rpm', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'tot_mileage_km': {
    label: 'Total Mileage',
    format: (value) => {
      return `${value} km`
    },
    get: (telemetry) => fromTelemetry('tot_mileage_km', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'fuel_level_perc': {
    label: 'Fuel Level Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('fuel_level_perc', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'eng_temp': {
    label: 'Eng. Temperature',
    format: (value) => {
      return `${value} ℃`
    },
    get: (telemetry) => fromTelemetry('eng_temp', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'trip_odo': {
    label: 'Trip Odometer',
    format: (value) => {
      return `${(value / 1000).toFixed(1)} km`
    },
    get: (telemetry) => fromTelemetry('trip_odo', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'movement': {
    label: 'Movement',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('movement', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'prog_number': {
    label: 'Program Number',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('prog_number', telemetry),
    chartType: null,
    category: 'general'
  },

  'module_id': {
    label: 'Module ID',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('module_id', telemetry),
    chartType: null,
    category: 'general'
  },

  // Check format
  'eng_total_time': {
    label: 'Eng. Total Time',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('eng_total_time', telemetry),
    chartType: null,
    category: 'engine'
  },

  // Check format
  'tot_mileage_cnt': {
    label: 'Total Mileage Count',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tot_mileage_cnt', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'fuel_consumed_cnt': {
    label: 'Fuel Consumed Count',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('fuel_consumed_cnt', telemetry),
    chartType: null,
    category: 'fuel'
  },

  'fuel_rate': {
    label: 'Fuel Rate',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('fuel_rate', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'hvb_charge_perc': {
    label: 'HV Battery Charge Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('hvb_charge_perc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'hvb_charging': {
    label: 'HV Battery Charging',
    format: (value) => {
      return value ? 'Yes' : 'No'
    },
    get: (telemetry) => fromTelemetry('hvb_charging', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'hvb_volts': {
    label: 'HV Battery Voltage',
    format: (value) => {
      return `${value} V`
    },
    get: (telemetry) => fromTelemetry('hvb_volts', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'hvb_current': {
    label: 'HV Battery Current (Amps)',
    format: (value) => {
      return `${value} A`
    },
    get: (telemetry) => fromTelemetry('hvb_current', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'hvb_power_kw': {
    label: 'HV Battery Power (kW)',
    format: (value) => {
      return `${value} kW`
    },
    get: (telemetry) => fromTelemetry('hvb_power_kw', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'hvb_consumed_kwh': {
    label: 'HV Battery Consumed Power (kWh)',
    format: (value) => {
      return `${value} kWh`
    },
    get: (telemetry) => fromTelemetry('hvb_consumed_kwh', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'parking_brake_active': {
    label: 'Parking Brake Active',
    format: (value) => {
      return value ? 'Yes' : 'No'
    },
    get: (telemetry) => fromTelemetry('parking_brake_active', telemetry),
    chartType: null,
    category: 'general'
  },

  'adblue_lvl_perc': {
    label: 'AdBlue Level Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('adblue_lvl_perc', telemetry),
    chartType: 'line',
    category: 'adblue'
  },

  'adblue_lvl': {
    label: 'AdBlue Level',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('adblue_lvl', telemetry),
    chartType: 'line',
    category: 'adblue'
  },

  'cont_state_flag': {
    label: 'Control State Flags',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('cont_state_flag', telemetry),
    chartType: null,
    category: 'general'
  },

  'agri_mach_flags': {
    label: 'Agricultural Machinery Flags',
    format: (value) => {
      return isNaN(value) ? value : '0x' + value.toString(16)
    },
    get: (telemetry) => fromTelemetry('agri_mach_flags', telemetry),
    chartType: null,
    category: 'agricultural'
  },

  'harvesting_time': {
    label: 'Harvesting Time',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('harvesting_time', telemetry),
    chartType: null,
    category: 'agricultural'
  },

  'area_of_harvest': {
    label: 'Area Of Harvest',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('area_of_harvest', telemetry),
    chartType: 'line',
    category: 'agricultural'
  },

  'mowing_eff': {
    label: 'Mowing Efficiency',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('mowing_eff', telemetry),
    chartType: 'line',
    category: 'agricultural'
  },

  'grain_mown_vol': {
    label: 'Grain Mow Volume',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('grain_mown_vol', telemetry),
    chartType: 'line',
    category: 'agricultural'
  },

  'grain_moist_perc': {
    label: 'Grain Moisture',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('grain_moist_perc', telemetry),
    chartType: 'line',
    category: 'agricultural'
  },

  'harv_drum_rpm': {
    label: 'Harvesting Drum RPM',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('harv_drum_rpm', telemetry),
    chartType: 'line',
    category: 'agricultural'
  },

  'harv_drum_gap': {
    label: 'Gap Under Harvesting Drum',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('harv_drum_gap', telemetry),
    chartType: 'line',
    category: 'agricultural'
  },

  'sec_state_flags': {
    label: 'Security State Flags',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('sec_state_flags', telemetry),
    chartType: null,
    category: 'general'
  },

  'trip_dist_km': {
    label: 'Trip Distance',
    format: (value) => {
      return `${value.toFixed(1)} km`
    },
    get: (telemetry) => fromTelemetry('trip_dist_km', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'tach_veh_speed': {
    label: 'Tacho Vehicle Speed',
    format: (value) => {
      return `${value} km/h`
    },
    get: (telemetry) => fromTelemetry('tach_veh_speed', telemetry),
    chartType: null,
    category: 'general'
  },

  'tach_driv_card': {
    label: 'Tacho Driver Card Presence',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tach_driv_card', telemetry),
    chartType: null,
    category: 'driver'
  },

  'bat_temp': {
    label: 'Battery Temperature',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('bat_temp', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'bat_lvl_perc': {
    label: 'Battery Level Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('bat_lvl_perc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'dtc_faults': {
    label: 'DTC Faults Count',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('dtc_faults', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'slope_of_arm': {
    label: 'Slope of Arm',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('slope_of_arm', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'rot_of_arm': {
    label: 'Rotation of Arm',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('rot_of_arm', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'eject_of_arm': {
    label: 'Eject of Arm',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('eject_of_arm', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'hrz_dist_arm': {
    label: 'Horizontal Distance Arm',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('hrz_dist_arm', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'height_of_arm': {
    label: 'Height Arm Above Ground',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('height_of_arm', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'drill_rpm': {
    label: 'Drill RPM',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('drill_rpm', telemetry),
    chartType: 'line',
    category: 'industrial'
  },

  'salt_spread': {
    label: 'Spread Salt',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('salt_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'bat_volts': {
    label: 'Battery Voltage',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('bat_volts', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'fine_salt_spread': {
    label: 'Spread Fine Grained Salt',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('fine_salt_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'coarse_salt': {
    label: 'Coarse Grained Salt',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('coarse_salt', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'spread_dimix': {
    label: 'Spread DiMix',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('spread_dimix', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'coarse_cal_spread': {
    label: 'Spread Coarse Grained Calcium',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('coarse_cal_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'sod_chlor_spread': {
    label: 'Spread Sodium Chloride',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('sod_chlor_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'cal_chlor_spread': {
    label: 'Spread Calcium Chloride',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('cal_chlor_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'mag_chlor_spread': {
    label: 'Spread Magnesium Chloride',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('mag_chlor_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'gravel_spread': {
    label: 'Amount Of Spread Gravel',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('gravel_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'sand_spread': {
    label: 'Amount Of Spread Sand',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('sand_spread', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'width_pour_left': {
    label: 'Width Pouring Left',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('width_pour_left', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'width_pour_right': {
    label: 'Width Pouring Right',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('width_pour_right', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'salt_work_hr': {
    label: 'Salt Spreader Working Hours',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('salt_work_hr', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'dist_during_salting': {
    label: 'Distance During Salting',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('dist_during_salting', telemetry),
    chartType: null,
    category: 'industrial'
  },

  'load_weight': {
    label: 'Load Weight',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('load_weight', telemetry),
    chartType: 'line',
    category: 'industrial'
  },

  'retard_load_perc': {
    label: 'Retarder Load',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('retard_load_perc', telemetry),
    chartType: null,
    category: 'engine'
  },

  'cruise_time': {
    label: 'Cruise Time',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('cruise_time', telemetry),
    chartType: null,
    category: 'engine'
  },

  'cng_status': {
    label: 'CNG Status',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('cng_status', telemetry),
    chartType: null,
    category: 'fuel'
  },

  'cng_used': {
    label: 'CNG Used',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('cng_used', telemetry),
    chartType: null,
    category: 'fuel'
  },

  'cng_lvl_perc': {
    label: 'CNG Level Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('cng_lvl_perc', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'eng_oil_lvl': {
    label: 'Eng. Oil Level',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('eng_oil_lvl', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  // Dallas temps can hold up to 4 value
  'dallas_temp': {
    label: 'Dallas Temperature',
    format: (value) => {
      return value + '℃'
    },
    get: (telemetry) => fromTelemetry('dallas_temp', telemetry),
    chartType: 'line',
    category: 'dallas_temp'
  },

  // Dallas temps can hold up to 4 value
  'dallas_temp_id': {
    label: 'Dallas Temperature ID',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('dallas_temp_id', telemetry),
    chartType: null,
    category: 'dallas_temp'
  },

  'sleep_mode': {
    label: 'Sleep Mode',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('sleep_mode', telemetry),
    chartType: null,
    category: 'general'
  },

  'doors_open': {
    label: 'Doors Open',
    format: (value) => {
      return value.map((status) => status.replace('_', ' ')).join(', ')
    },
    get: (telemetry) => fromTelemetry('doors_open', telemetry),
    chartType: null,
    category: 'general'
  },

  'seatbelts_fastened': {
    label: 'Seatbelts Fastened',
    format: (value) => {
      return value.map((status) => status.replace('_', ' ')).join(', ')
    },
    get: (telemetry) => fromTelemetry('seatbelts_fastened', telemetry),
    chartType: null,
    category: 'general'
  },

  'lights_on': {
    label: 'Lights On',
    format: (value) => {
      return value.map((status) => status.replace('_', ' ')).join(', ')
    },
    get: (telemetry) => fromTelemetry('lights_on', telemetry),
    chartType: null,
    category: 'general'
  },

  'central_lock_open': {
    label: 'Central Lock Open',
    format: (value) => {
      return value ? 'Yes' : 'No'
    },
    get: (telemetry) => fromTelemetry('central_lock_open', telemetry),
    chartType: null,
    category: 'general'
  },

  'brake_pushed': {
    label: 'Brake Pushed',
    format: (value) => {
      return value ? 'Yes' : 'No'
    },
    get: (telemetry) => fromTelemetry('brake_pushed', telemetry),
    chartType: null,
    category: 'general'
  },

  // Value is array
  'axl_load': {
    label: 'Axle Load',
    format: (value) => {
      return `${value[0]}`
    },
    get: (telemetry) => fromTelemetry('axl_load', telemetry),
    chartType: 'line',
    category: 'general'
  },

  // Value is array
  'driver_states': {
    label: 'Driver State',
    format: (value) => {
      return value === '00000000000000000000000000000000' ? 'Unknown' : value
    },
    get: (telemetry) => fromTelemetry('driver_states', telemetry),
    chartType: null,
    category: 'driver_state'
  },

  // Value is array for driver 1 and 2
  'driver_cont_times': {
    label: 'Continuous Driving Time',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('driver_cont_times', telemetry),
    chartType: null,
    category: 'driver_state'
  },

  // Value is array for driver 1 and 2
  'driver_break_times': {
    label: 'Cumulative Break Time',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('driver_break_times', telemetry),
    chartType: null,
    category: 'driver_state'
  },

  // Value is array for driver 1 and 2
  'driver_act_dur': {
    label: 'Duration of Selected Activity',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('driver_act_dur', telemetry),
    chartType: null,
    category: 'driver_state'
  },

  // Value is array for driver 1 and 2
  'driver_cumu_times': {
    label: 'Cumulative Driving Time',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('driver_cumu_times', telemetry),
    chartType: null,
    category: 'driver_state'
  },

  // Value is array for driver 1 and 2
  'driver_ids': {
    label: 'Driver Id\'s',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('driver_ids', telemetry),
    chartType: null,
    category: 'driver_state'
  },

  // Is added by LDPS or DPPS
  'pp_idling': {
    label: 'Idling (Detected via Processing)',
    format: (value) => {
      return value ? 'Yes' : 'No'
    },
    get: (telemetry) => fromTelemetry('pp_idling', telemetry),
    chartType: null,
    category: 'engine'
  },

  // TODO :: Get correct label
  'rec_in_flash': {
    label: 'Rec In Flash',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('rec_in_flash', telemetry),
    chartType: null,
    category: 'general'
  },

  'ibutton_auth': {
    label: 'IButton Authorization',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ibutton_auth', telemetry),
    chartType: null,
    category: 'driver'
  },

  'sd_present': {
    label: 'SD Status',
    format: (value) => {
      return value === 0 ? 'Not Present' : 'Present'
    },
    get: (telemetry) => fromTelemetry('sd_present', telemetry),
    chartType: null,
    category: 'general'
  },

  'fuel_used_gps': {
    label: 'Fuel Used GPS',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('fuel_used_gps', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'fuel_rate_gps': {
    label: 'Fuel Rate GPS',
    format: (value) => {
      return `${value} ℓ/100km`
    },
    get: (telemetry) => fromTelemetry('fuel_rate_gps', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'total_odo': {
    label: 'Total Odometer',
    format: (value) => {
      return `${(value / 1000).toFixed(1)} km`
    },
    get: (telemetry) => fromTelemetry('total_odo', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'accel_x': {
    label: 'Acceleration X',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('accel_x', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'accel_y': {
    label: 'Acceleration Y',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('accel_y', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'accel_z': {
    label: 'Acceleration Z',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('accel_z', telemetry),
    chartType: 'line',
    category: 'general'
  },

  // TODO :: Check values if 0 and 1 return 'connected' and 'not connected'
  'charger_conn': {
    label: 'Charger Connected',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('charger_conn', telemetry),
    chartType: null,
    category: 'general'
  },

  'rfid': {
    label: 'RFID',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('rfid', telemetry),
    chartType: null,
    category: 'general'
  },

  'user_id': {
    label: 'User ID',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('user_id', telemetry),
    chartType: null,
    category: 'general'
  },

  // TODO :: Get correct Labels for obd sensors
  'obd_accum_mil': {
    label: 'Accumulative Mileage',
    format: (value) => {
      return `${value} km`
    },
    get: (telemetry) => fromTelemetry('obd_accum_mil', telemetry),
    chartType: null,
    category: 'odometer'
  },

  // TODO :: Check formats of fuel related obd sensors
  'obd_inst_fuel': {
    label: 'Inst Fuel',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('obd_inst_fuel', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'obd_avg_fuel': {
    label: 'Average Fuel',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('obd_avg_fuel', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'obd_drive_time': {
    label: 'Drive Time',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_drive_time', telemetry),
    chartType: null,
    category: 'general'
  },

  'obd_speed': {
    label: 'Speed',
    format: (value) => {
      return `${value} km/h`
    },
    get: (telemetry) => fromTelemetry('obd_speed', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'obd_power_load': {
    label: 'Power Load',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_power_load', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'obd_water_temp': {
    label: 'Water Temperature',
    format: (value) => {
      return `${value} ℃`
    },
    get: (telemetry) => fromTelemetry('obd_water_temp', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'obd_throttle_perc': {
    label: 'Throttle Percentage',
    format: (value) => {
      return `${value} %`
    },
    get: (telemetry) => fromTelemetry('obd_throttle_perc', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'obd_engine_speed': {
    label: 'Engine Speed',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_engine_speed', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'obd_battery_volts': {
    label: 'Battery Volts',
    format: (value) => {
      return `${value} V`
    },
    get: (telemetry) => fromTelemetry('obd_battery_volts', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'obd_diag_code_01': {
    label: 'Diagnostic Code 1',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_diag_code_01', telemetry),
    chartType: null,
    category: 'general'
  },

  'obd_diag_code_02': {
    label: 'Diagnostic Code 2',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_diag_code_02', telemetry),
    chartType: null,
    category: 'general'
  },

  'obd_diag_code_03': {
    label: 'Diagnostic Code 3',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_diag_code_03', telemetry),
    chartType: null,
    category: 'general'
  },

  'obd_diag_code_04': {
    label: 'Diagnostic Code 4',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('obd_diag_code_04', telemetry),
    chartType: null,
    category: 'general'
  },

  'tpms_device_stat': {
    label: 'Device Status',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_device_stat', telemetry),
    chartType: null,
    category: 'general'
  },

  'tpms_no_of_wheels': {
    label: 'Number Of Wheels',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_no_of_wheels', telemetry),
    chartType: null,
    category: 'general'
  },

  'tpms_lf_press': {
    label: 'Left Front Pressure',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_lf_press', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_lf_temp': {
    label: 'Left Front Temperature',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_lf_temp', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_lf_state': {
    label: 'Left Front State',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_lf_state', telemetry),
    chartType: null,
    category: 'general'
  },

  'tpms_rf_press': {
    label: 'Right Front Pressure',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_rf_press', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_rf_temp': {
    label: 'Right Front Temperature',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_rf_temp', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_rf_state': {
    label: 'Right Front State',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_rf_state', telemetry),
    chartType: null,
    category: 'general'
  },

  'tpms_lr_press': {
    label: 'Left Rear Pressure',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_lr_press', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_lr_temp': {
    label: 'Left Rear Temperature',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_lr_temp', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_lr_state': {
    label: 'Left Rear State',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_lr_state', telemetry),
    chartType: null,
    category: 'general'
  },

  'tpms_rr_press': {
    label: 'Right Rear Pressure',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_rr_press', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_rr_temp': {
    label: 'Right Rear Temperature',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_rr_temp', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'tpms_rr_state': {
    label: 'Right Rear State',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('tpms_rr_state', telemetry),
    chartType: null,
    category: 'general'
  },

  'pulse_cnt_din': {
    label: 'DIN Pulse Counter',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('pulse_cnt_din', telemetry),
    chartType: null,
    category: 'io'
  },

  'ble_temp': {
    label: 'BLE Temperature',
    format: (value) => {
      return value + '℃'
    },
    get: (telemetry) => fromTelemetry('ble_temp', telemetry),
    chartType: 'line',
    category: 'ble_temperature'
  },

  'ble_temp_errors': {
    label: 'BLE Temperatures Error',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ble_temp_errors', telemetry),
    chartType: null,
    category: 'ble_temperature'
  },

  'eye_temp': {
    label: 'EYE Temperature',
    format: (value) => {
      return value + '℃'
    },
    get: (telemetry) => fromTelemetry('eye_temp', telemetry),
    chartType: 'line',
    category: 'eye_temperature'
  },

  'eye_magnet': {
    label: 'EYE Magnet',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('eye_magnet', telemetry),
    chartType: null,
    category: 'general'
  },

  'eye_magnet_count': {
    label: 'EYE Magnet Counter',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('eye_magnet_count', telemetry),
    chartType: null,
    category: 'general'
  },

  'eye_pitch': {
    label: 'EYE Pitch Angle',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('eye_pitch', telemetry),
    chartType: null,
    category: 'general'
  },

  'eye_roll': {
    label: 'EYE Roll Angle',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('eye_roll', telemetry),
    chartType: null,
    category: 'general'
  },

  'eye_humidity': {
    label: 'EYE Humidity',
    format: (value) => {
      return value + '%'
    },
    get: (telemetry) => fromTelemetry('eye_humidity', telemetry),
    chartType: 'line',
    category: 'humidity'
  },

  'pto_tilt_status': {
    label: 'PTO Tilt Status',
    format: (value) => {
      return value[0] === 1 ? 'PTO On' : 'PTO Off'
    },
    get: (telemetry) => fromTelemetry('pto_tilt_status', telemetry),
    chartType: null,
    category: 'general'
  },

  'pto_analog_status': {
    label: 'PTO Analog Status',
    format: (value) => {
      return [
        analogToPtoStatus(1, value),
        analogToPtoStatus(2, value),
        analogToPtoStatus(3, value),
        analogToPtoStatus(4, value)
      ].filter((s) => s !== '').join(', ')
    },
    get: (telemetry) => fromTelemetry('pto_analog_status', telemetry),
    chartType: null,
    category: 'general'
  },

  'ble_bat_perc': {
    label: 'BLE Battery Percentage',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ble_bat_perc', telemetry),
    chartType: 'line',
    category: 'ble_battery'
  },

  'ble_humidity': {
    label: 'BLE Humidity',
    format: (value) => {
      return value + '%RH'
    },
    get: (telemetry) => fromTelemetry('ble_humidity', telemetry),
    chartType: 'line',
    category: 'ble_temperature'
  },

  'ble_humidity_errors': {
    label: 'BLE Humidity Errors',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ble_humidity_errors', telemetry),
    chartType: null,
    category: 'ble_temperature'
  },

  // TODO :: Check that sensor names for ble sensors is correct.
  'ble_fuel_level': {
    label: 'BLE Fuel Level',
    format: (value) => {
      return value + 'ℓ'
    },
    get: (telemetry) => fromTelemetry('ble_fuel_level', telemetry),
    chartType: 'line',
    category: 'ble_fuel'
  },

  'ble_fuel_freq': {
    label: 'BLE Fuel Frequency',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ble_fuel_freq', telemetry),
    chartType: 'line',
    category: 'ble_fuel'
  },

  'ble_luminosity': {
    label: 'BLE Luminosity',
    format: (value) => {
      return value
    },
    get: (telemetry) => fromTelemetry('ble_luminosity', telemetry),
    chartType: 'line',
    category: 'ble_luminosity'
  },

  'lls_fuel': {
    label: 'LLS Fuel',
    format: (value) => {
      return value + 'ℓ'
    },
    get: (telemetry) => fromTelemetry('lls_fuel', telemetry),
    chartType: 'line',
    category: 'lls_fuel'
  },

  'lls_fuel_level': {
    label: 'LLS Fuel Level',
    format: (value) => {
      return value + 'ℓ'
    },
    get: (telemetry) => fromTelemetry('lls_fuel_level', telemetry),
    chartType: 'line',
    category: 'lls_fuel_level'
  },

  'lls_temp': {
    label: 'LLS Temperature',
    format: (value) => {
      return value + '℃'
    },
    get: (telemetry) => fromTelemetry('lls_temp', telemetry),
    chartType: 'line',
    category: 'lls_temp'
  },

  'odometer': {
    label: 'Odometer',
    format: (value) => {
      return `${value} km`
    },
    get: (telemetry) => fromTelemetry('odometer', telemetry),
    chartType: null,
    category: 'odometer'
  },

  'engine_hours': {
    label: 'Engine Hours',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('engine_hours', telemetry),
    chartType: null,
    category: 'engine'
  },

  'pulse_counter': {
    label: 'Pulse Counter',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('pulse_counter', telemetry),
    chartType: null,
    category: 'io'
  },

  'adc1': {
    label: 'ADC1',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('adc1', telemetry),
    chartType: null,
    category: 'io'
  },

  'adc2': {
    label: 'ADC2',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('adc2', telemetry),
    chartType: null,
    category: 'io'
  },

  'private_mode': {
    label: 'Private Mode',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('private_mode', telemetry),
    chartType: null,
    category: 'io'
  },

  'inside_fences': {
    label: 'Inside Fences',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('inside_fences', telemetry),
    chartType: null,
    category: 'general'
  },

  'outside_fences': {
    label: 'Outside Fences',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('outside_fences', telemetry),
    chartType: null,
    category: 'general'
  },

  'fuel_level': {
    label: 'Fuel Level',
    format: (value) => {
      return `${value} ℓ`
    },
    get: (telemetry) => fromTelemetry('fuel_level', telemetry),
    chartType: 'line',
    category: 'fuel'
  },

  'user_alarms': {
    label: 'User Alarms',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('user_alarms', telemetry),
    chartType: null,
    category: 'general'
  },

  'gps_mileage': {
    label: 'GPS Mileage',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('gps_mileage', telemetry),
    chartType: null,
    category: 'gps'
  },

  'operator_no_1': {
    label: 'Operator No 1',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('operator_no_1', telemetry),
    chartType: null,
    category: 'general'
  },

  'operator_no_2': {
    label: 'Operator No 2',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('operator_no_2', telemetry),
    chartType: null,
    category: 'general'
  },

  'temp_01': {
    label: 'Temperature 1',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('temp_01', telemetry),
    chartType: null,
    category: 'general'
  },

  'ambient_temp': {
    label: 'Ambient Temperature',
    format: (value) => {
      return `${value}`
    },
    get: (telemetry) => fromTelemetry('ambient_temp', telemetry),
    chartType: null,
    category: 'general'
  },

  // Decoded by GWS from the Henrey R2D CAN profile (henrey-r2d-bcan).
  'batt_ext_volts': {
    label: 'Battery External Voltage',
    format: canNumber(1, 'V'),
    get: (telemetry) => fromTelemetry('batt_ext_volts', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_pack_amps': {
    label: 'Pack Current',
    format: canNumber(1, 'A'),
    get: (telemetry) => fromTelemetry('batt_pack_amps', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_soc': {
    label: 'State of Charge',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'cell_temp_high': {
    label: 'Highest Cell Temperature',
    format: canNumber(null, '℃'),
    get: (telemetry) => fromTelemetry('cell_temp_high', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'cell_temp_high_sensor': {
    label: 'Sensor Reporting the Highest Temperature',
    format: canNumber(null, ''),
    get: (telemetry) => fromTelemetry('cell_temp_high_sensor', telemetry),
    chartType: null,
    category: 'battery'
  },

  'cell_temp_low': {
    label: 'Lowest Cell Temperature',
    format: canNumber(null, '℃'),
    get: (telemetry) => fromTelemetry('cell_temp_low', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'cell_temp_low_sensor': {
    label: 'Sensor Reporting the Lowest Temperature',
    format: canNumber(null, ''),
    get: (telemetry) => fromTelemetry('cell_temp_low_sensor', telemetry),
    chartType: null,
    category: 'battery'
  },

  'can_speed': {
    label: 'Vehicle Speed (CAN)',
    format: canNumber(1, 'km/h'),
    get: (telemetry) => fromTelemetry('can_speed', telemetry),
    chartType: 'line',
    category: 'general'
  },

  'can_odometer': {
    label: 'Total Mileage (CAN)',
    format: canNumber(1, 'km'),
    get: (telemetry) => fromTelemetry('can_odometer', telemetry),
    chartType: 'line',
    category: 'odometer'
  },

  'handbrake': {
    label: 'Handbrake Applied',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('handbrake', telemetry),
    chartType: null,
    category: 'general'
  },

  'eps_fault': {
    label: 'Electric Power Steering Fault',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('eps_fault', telemetry),
    chartType: null,
    category: 'general'
  },

  'brake_booster_fault': {
    label: 'Vacuum Brake Booster Fault',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('brake_booster_fault', telemetry),
    chartType: null,
    category: 'general'
  },

  'brake_fluid_low': {
    label: 'Brake Fluid Low',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('brake_fluid_low', telemetry),
    chartType: null,
    category: 'general'
  },

  'door_open': {
    label: 'Door Open',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('door_open', telemetry),
    chartType: null,
    category: 'general'
  },

  'seatbelt_unfastened': {
    label: 'Seat Belt Unfastened While Started',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('seatbelt_unfastened', telemetry),
    chartType: null,
    category: 'general'
  },

  'position_lamps_on': {
    label: 'Position Lamps On',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('position_lamps_on', telemetry),
    chartType: null,
    category: 'general'
  },

  'headlamps_on': {
    label: 'Headlamps On',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('headlamps_on', telemetry),
    chartType: null,
    category: 'general'
  },

  'airbag_passenger_on': {
    label: 'Passenger Airbag Enabled',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('airbag_passenger_on', telemetry),
    chartType: null,
    category: 'general'
  },

  'vehicle_status': {
    label: 'Vehicle Status',
    format: canEnum({
      accessory: 'Accessory',
      off: 'Off',
      ready: 'Ready'
    }),
    get: (telemetry) => fromTelemetry('vehicle_status', telemetry),
    chartType: null,
    category: 'general'
  },

  'turn_signals': {
    label: 'Turn Signals',
    format: canEnum({
      hazard: 'Hazard',
      left: 'Left',
      off: 'Off',
      right: 'Right'
    }),
    get: (telemetry) => fromTelemetry('turn_signals', telemetry),
    chartType: null,
    category: 'general'
  },

  'supply_12v_status': {
    label: '12V Supply Status',
    format: canEnum({
      ok: 'OK',
      undervoltage_1: 'Undervoltage (level 1)',
      undervoltage_2: 'Undervoltage (level 2)'
    }),
    get: (telemetry) => fromTelemetry('supply_12v_status', telemetry),
    chartType: null,
    category: 'battery'
  },

  'gear': {
    label: 'Gear',
    format: canEnum({
      drive: 'Drive',
      neutral: 'Neutral',
      reverse: 'Reverse'
    }),
    get: (telemetry) => fromTelemetry('gear', telemetry),
    chartType: null,
    category: 'engine'
  },

  'speed_mode': {
    label: 'Speed Mode',
    format: canEnum({
      high_speed: 'High speed',
      low_speed: 'Low speed'
    }),
    get: (telemetry) => fromTelemetry('speed_mode', telemetry),
    chartType: null,
    category: 'engine'
  },

  'drive_ready': {
    label: 'Ready to Drive',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('drive_ready', telemetry),
    chartType: null,
    category: 'engine'
  },

  'motor_rpm': {
    label: 'Motor Speed',
    format: canNumber(null, 'rpm'),
    get: (telemetry) => fromTelemetry('motor_rpm', telemetry),
    chartType: 'line',
    category: 'engine'
  },

  'motor_fault_code': {
    label: 'Motor Fault Code',
    format: canNumber(null, ''),
    get: (telemetry) => fromTelemetry('motor_fault_code', telemetry),
    chartType: null,
    category: 'engine'
  },

  'low_power_mode': {
    label: 'Low Power Mode',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('low_power_mode', telemetry),
    chartType: null,
    category: 'battery'
  },

  'charge_request': {
    label: 'Charging Request',
    format: canEnum({
      start_charging: 'Start charging',
      start_heating: 'Start heating',
      stop_charging: 'Stop charging'
    }),
    get: (telemetry) => fromTelemetry('charge_request', telemetry),
    chartType: null,
    category: 'battery'
  },

  'charging_state': {
    label: 'Charging State',
    format: canEnum({
      charge_complete: 'Charge complete',
      charging_driving: 'Charging (driving)',
      charging_parked: 'Charging (parked)',
      not_charging: 'Not charging'
    }),
    get: (telemetry) => fromTelemetry('charging_state', telemetry),
    chartType: null,
    category: 'battery'
  },

  'supply_12v_topup': {
    label: 'Smart 12V Top-up Status',
    format: canEnum({
      allow_charge: 'Allow charge',
      charging: 'Charging',
      standby: 'Standby',
      stop_charge: 'Stop charge'
    }),
    get: (telemetry) => fromTelemetry('supply_12v_topup', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_power_kw': {
    label: 'Pack Power',
    format: canNumber(2, 'kW'),
    get: (telemetry) => fromTelemetry('batt_power_kw', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'range_est_km': {
    label: 'Remaining Range (est.)',
    format: canNumber(0, 'km'),
    get: (telemetry) => fromTelemetry('range_est_km', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  // Decoded by GWS from the Valt V1 Max CAN profile (valt-v1max).
  'batt_1_discharging': {
    label: 'Pack 1 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_1_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_2_discharging': {
    label: 'Pack 2 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_2_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_3_discharging': {
    label: 'Pack 3 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_3_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_4_discharging': {
    label: 'Pack 4 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_4_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_5_discharging': {
    label: 'Pack 5 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_5_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_6_discharging': {
    label: 'Pack 6 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_6_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_7_discharging': {
    label: 'Pack 7 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_7_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_8_discharging': {
    label: 'Pack 8 Discharging',
    format: canBoolean,
    get: (telemetry) => fromTelemetry('batt_8_discharging', telemetry),
    chartType: null,
    category: 'battery'
  },

  'batt_1_soc': {
    label: 'Pack 1 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_1_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_2_soc': {
    label: 'Pack 2 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_2_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_3_soc': {
    label: 'Pack 3 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_3_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_4_soc': {
    label: 'Pack 4 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_4_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_5_soc': {
    label: 'Pack 5 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_5_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_6_soc': {
    label: 'Pack 6 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_6_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_7_soc': {
    label: 'Pack 7 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_7_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_8_soc': {
    label: 'Pack 8 Charge Level',
    format: canNumber(null, '%'),
    get: (telemetry) => fromTelemetry('batt_8_soc', telemetry),
    chartType: 'line',
    category: 'battery'
  },

  'batt_output_amps': {
    label: 'Total Pack Output Current',
    format: canNumber(3, 'A'),
    get: (telemetry) => fromTelemetry('batt_output_amps', telemetry),
    chartType: 'line',
    category: 'battery'
  }
}

/**
 * Returns a sensor's value together with a timestamp/local_time.
 *
 * @param name String
 * @param telemetry Object
 *
 * @return Object
 */
export const withTimestamp = (name, telemetry) => {
  if (typeof sensors[name] !== 'undefined') {
    return sensors[name].get(telemetry)
  } else {
    return null
  }
}

export const sensorAvailable = (sensor) => {
  return sensors.hasOwnProperty(sensor)
}

export const fmtSensorValue = (sensor, value) => {
  if (sensors.hasOwnProperty(sensor)) {
    return sensors[sensor]['format'](value)
  } else {
    return null
  }
}

export const sensorLabel = (sensor) => {
  if (sensors.hasOwnProperty(sensor)) {
    return sensors[sensor]['label']
  } else {
    return null
  }
}

export const zoneEventDetails = (events) => {
  if (events.length === 0) {
    return ''
  }

  let results = []
  events.forEach((event) => {
    switch (event) {
      case 'ZONE:ENTER':
        results.push('Entered')
        break

      case 'ZONE:EXIT':
        results.push('Departed')
        break

      case 'ZONE:NO_GO':
        results.push('No Go!')
        break

      case 'ZONE:FORBIDDEN_EXIT':
        results.push('Forbidden Exit!')
        break

      case 'ZONE:SPEEDING':
        results.push('Speeding')
        break

      case 'ZONE:DELAYED_STOP':
        results.push('Delayed Stop')
        break

      case 'ZONE:FORBIDDEN_STOP':
        results.push('Forbidden Stop')
        break
    }
  })

  if (results.length > 0) {
    return '(' + results.join(', ') + ')'
  } else {
    return ''
  }
}

export const zoneEventStyle = (events) => {
  if (events.length === 0) {
    return ''
  }

  let level = 0
  events.forEach((event) => {
    switch (event) {
      case 'ZONE:NO_GO':
        level += 2
        break

      case 'ZONE:FORBIDDEN_EXIT':
        level += 2
        break

      case 'ZONE:SPEEDING':
        level += 1
        break

      case 'ZONE:DELAYED_STOP':
        level += 1
        break

      case 'ZONE:FORBIDDEN_STOP':
        level += 2
        break
    }
  })

  if (level === 1) {
    return 'color: #fb8c00'
  } else if (level > 1) {
    return 'color: #e53935'
  } else {
    return ''
  }
}



// WEBPACK FOOTER //
// ./src/common/unitSensors.js